import React from 'react';
import Tooltip from '@/components/ui/Tooltip';
import { RiEyeLine as PreviewIcon } from 'react-icons/ri';
import { DocumentsApi } from '@/api';
import DocumentPreviewModal from '@/components/documents/DocumentPreviewModal';
import type { DocumentItem, DocumentWithStatus } from '@/types/document';
import {
  RiArrowDownSLine as ArrowDownIcon,
  RiArrowUpSLine as ArrowUpIcon,
  RiFilePdf2Line as PdfIcon,
  RiFileWordLine as WordIcon,
  RiFileTextLine as TextIcon,
  RiSearchEyeLine as EmptyIcon,
  RiCloseLine as CloseIcon,
} from 'react-icons/ri';

export interface SourceItem {
  text: string;
  score: number; // relevance/semantic score
  queryText: string;
  documentId?: string;
  docPriorityScore?: number;
  docPriorityLabel?: string;
}

export interface SourcesSidebarProps {
  sources: Record<string, SourceItem[]>;
  isLoading?: boolean;
  caseId?: string;
  onClose?: () => void;
}

const stopWords = new Set([
  'a',
  'about',
  'above',
  'after',
  'again',
  'against',
  'all',
  'am',
  'an',
  'and',
  'any',
  'are',
  'as',
  'at',
  'be',
  'because',
  'been',
  'before',
  'being',
  'below',
  'between',
  'both',
  'but',
  'by',
  'can',
  'cannot',
  'could',
  'did',
  'do',
  'does',
  'doing',
  'down',
  'during',
  'each',
  'few',
  'for',
  'from',
  'further',
  'had',
  'has',
  'have',
  'having',
  'he',
  'her',
  'here',
  'hers',
  'herself',
  'him',
  'himself',
  'his',
  'how',
  'i',
  'if',
  'in',
  'into',
  'is',
  'it',
  'its',
  'itself',
  'me',
  'more',
  'most',
  'my',
  'myself',
  'no',
  'nor',
  'not',
  'of',
  'off',
  'on',
  'once',
  'only',
  'or',
  'other',
  'our',
  'ours',
  'ourselves',
  'out',
  'over',
  'own',
  'same',
  'she',
  'should',
  'so',
  'some',
  'such',
  'than',
  'that',
  'the',
  'their',
  'theirs',
  'them',
  'themselves',
  'then',
  'there',
  'these',
  'they',
  'this',
  'those',
  'through',
  'to',
  'too',
  'under',
  'until',
  'up',
  'very',
  'was',
  'we',
  'were',
  'what',
  'when',
  'where',
  'which',
  'while',
  'who',
  'whom',
  'why',
  'with',
  'would',
  'you',
  'your',
  'yours',
  'yourself',
  'yourselves',
]);

function getFileIcon(filename: string) {
  if (filename.toLowerCase().endsWith('.pdf'))
    return <PdfIcon className="text-red-500" />;
  if (
    filename.toLowerCase().endsWith('.doc') ||
    filename.toLowerCase().endsWith('.docx')
  )
    return <WordIcon className="text-blue-500" />;
  return <TextIcon className="text-neutral-500" />;
}

// Priority badge based on backend-provided label
function PriorityBadge({ label }: { label?: string }) {
  if (!label) return null;
  const norm = label.toLowerCase();
  const styleMap: Record<string, string> = {
    cv: 'bg-green-100 text-green-800',
    award: 'bg-amber-100 text-amber-800',
    publication: 'bg-indigo-100 text-indigo-800',
    employment: 'bg-blue-100 text-blue-800',
    recommendation: 'bg-sky-100 text-sky-800',
  };
  const cls = styleMap[norm] || 'bg-neutral-100 text-neutral-700';
  return (
    <span
      className={`ml-2 px-2 py-0.5 text-[10px] font-medium rounded-full ${cls}`}
    >
      {label}
    </span>
  );
}

function buildHighlightRegex(queryText: string): RegExp | null {
  if (!queryText) return null;
  const parts = Array.from(
    new Set(queryText.toLowerCase().split(/\s+/))
  ).filter(w => w.length > 1 && !stopWords.has(w));
  if (parts.length === 0) return null;
  const escaped = parts.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  // Single capturing group; internal alternation is non-capturing to avoid duplicate segments in split()
  try {
    return new RegExp(`(\\b(?:${escaped.join('|')})\\b)`, 'gi');
  } catch {
    return null;
  }
}

function Highlighted({ text, queryText }: { text: string; queryText: string }) {
  const rx = React.useMemo(() => buildHighlightRegex(queryText), [queryText]);
  if (!rx) return <>{text}</>;
  const parts = text.split(rx); // pattern: [non, match, non, match, ...]
  return (
    <>
      {parts.map((part, idx) =>
        idx % 2 === 1 ? (
          <span key={idx} className="bg-yellow-200">
            {part}
          </span>
        ) : (
          <React.Fragment key={idx}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

const SourcesSidebar: React.FC<SourcesSidebarProps> = ({
  sources,
  isLoading,
  caseId,
  onClose,
}) => {
  const [expanded, setExpanded] = React.useState<number[]>([0]);

  // Sort filenames using backend docPriorityScore (max of items) then fallback to name
  const filenames = React.useMemo(() => {
    return Object.keys(sources).sort((a, b) => {
      const arrA = sources[a] || [];
      const arrB = sources[b] || [];
      const maxA = Math.max(-1, ...arrA.map(s => s.docPriorityScore ?? -1));
      const maxB = Math.max(-1, ...arrB.map(s => s.docPriorityScore ?? -1));
      if (maxB !== maxA) return maxB - maxA;
      return a.localeCompare(b);
    });
  }, [sources]);

  const hasSources = filenames.length > 0;

  // Case documents for preview lookup
  const [docs, setDocs] = React.useState<DocumentItem[]>([]);
  const [docsLoading, setDocsLoading] = React.useState(false);
  const [previewDoc, setPreviewDoc] = React.useState<DocumentWithStatus | null>(
    null
  );

  React.useEffect(() => {
    const shouldLoad = !!caseId && hasSources;
    if (!shouldLoad) return;
    let mounted = true;
    setDocsLoading(true);
    DocumentsApi.listCaseDocuments(caseId!)
      .then(list => {
        if (!mounted) return;
        setDocs(list || []);
      })
      .catch(() => {
        if (!mounted) return;
        setDocs([]);
      })
      .finally(() => {
        if (!mounted) return;
        setDocsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [caseId, hasSources]);

  const toggle = (idx: number) => {
    setExpanded(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  return (
    <div className="ml-4 w-80 flex flex-col max-h-[calc(100vh-100px)] overflow-auto sticky top-[58px] bottom-0">
      <div className="rounded-lg px-2 pb-4 relative">
        <div className="sticky top-0 bg-neutral-50 py-4 flex justify-between items-center mb-2 z-40 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-800">Sources</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-neutral-200 text-neutral-600 hover:text-neutral-800 transition-colors"
              aria-label="Close sources sidebar"
              title="Close"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col bg-white items-center justify-center h-48 border rounded border-neutral-200">
            <div className="w-16 h-16 border-4 border-primary-500 border-dashed rounded-full animate-spin" />
            <p className="mt-4 text-primary-600">Loading...</p>
          </div>
        ) : hasSources ? (
          <div>
            {filenames.map((filename, idx) => (
              <div
                key={filename + idx}
                className={`mb-2 border rounded p-2 px-3 bg-white border-neutral-200`}
              >
                {/* Header row: filename (truncates) + actions (always visible) */}
                <div className="w-full flex items-center gap-2">
                  <button
                    onClick={() => toggle(idx)}
                    className="cursor-pointer flex-1 min-w-0 flex items-center text-left focus:outline-none"
                    aria-label={expanded.includes(idx) ? 'Collapse' : 'Expand'}
                  >
                    <span className="text-2xl mr-2 flex-shrink-0">
                      {getFileIcon(filename)}
                    </span>
                    <Tooltip
                      content={filename}
                      triggerClassName="flex-1 min-w-0"
                    >
                      <span className="text-neutral-700 font-medium text-sm truncate">
                        {filename}
                      </span>
                    </Tooltip>
                    <PriorityBadge
                      label={sources[filename]?.[0]?.docPriorityLabel}
                    />
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Tooltip content="Preview">
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          if (!docs || docs.length === 0) return;
                          const match = docs.find(d => d.fileName === filename);
                          if (!match) return;
                          const asPreview: DocumentWithStatus = {
                            id: match.id,
                            fileName: match.fileName,
                            filePath: match.filePath,
                            mimeType:
                              (match as any).mimeType ||
                              'application/octet-stream',
                            fileSize: (match as any).fileSize || 0,
                            documentType: (match as any).documentType || '',
                            processingStatus: 'COMPLETED',
                            tags: (match as any).tags || [],
                            categoryConfidence:
                              (match as any).categoryConfidence || 0,
                            ocrMethod: (match as any).ocrMethod || '',
                            isProcessed: true,
                            role: (match as any).role || '',
                            addedAt: (match as any).addedAt || '',
                            uploadedAt: (match as any).uploadedAt || '',
                            lastModified: (match as any).lastModified || '',
                          };
                          setPreviewDoc(asPreview);
                        }}
                        className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                        title="Preview"
                        aria-label="Preview document"
                        disabled={!caseId || docsLoading}
                      >
                        <PreviewIcon className="text-lg" />
                      </button>
                    </Tooltip>
                    <Tooltip
                      content={expanded.includes(idx) ? 'Collapse' : 'Expand'}
                    >
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          toggle(idx);
                        }}
                        className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                        title={expanded.includes(idx) ? 'Collapse' : 'Expand'}
                        aria-label={
                          expanded.includes(idx) ? 'Collapse' : 'Expand'
                        }
                      >
                        {expanded.includes(idx) ? (
                          <ArrowUpIcon className="text-lg" />
                        ) : (
                          <ArrowDownIcon className="text-lg" />
                        )}
                      </button>
                    </Tooltip>
                  </div>
                </div>
                {/* Expanded content */}
                {expanded.includes(idx) && (
                  <div className="mt-2 space-y-3">
                    {(sources[filename] || []).map((s, sIdx) => (
                      <div className="mb-2" key={sIdx}>
                        <div className="flex items-center mb-1">
                          <span className="font-bold text-neutral-700 mr-1 text-sm">
                            Quote
                          </span>
                        </div>
                        <blockquote className="border-l-4 m-0 border-primary-500 pl-3 italic text-neutral-600 text-xs">
                          <Highlighted text={s.text} queryText={s.queryText} />
                        </blockquote>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 p-6 text-center rounded-lg bg-white border border-neutral-200">
            <div>
              <EmptyIcon className="text-primary-400 text-4xl block mx-auto mb-3" />
              <p className="text-sm text-neutral-600 mb-4">
                Select text in the editor to discover related sources and view
                them here.
              </p>
            </div>
          </div>
        )}
        {previewDoc && (
          <DocumentPreviewModal
            document={previewDoc}
            isOpen={!!previewDoc}
            onClose={() => setPreviewDoc(null)}
          />
        )}
      </div>
    </div>
  );
};

export default SourcesSidebar;
